<div class="contentInner">
  <h1 class="sectionHeader">Using Dash with Microsoft 365</h1>

  <p class="intro">
    Before connecting Dash to your Microsoft 365 account you need to make sure that your meeting rooms are set up and managed in your Office Organization.
  </p>

  <h2>Setting up Microsoft 365</h2>
  <p>
    The recommended procedure is to set up each of your meeting rooms as a <a href="https://docs.microsoft.com/en-us/microsoft-365/admin/manage/room-and-equipment-mailboxes?view=o365-worldwide">
    Room and equipment Mailbox</a>.  Room and Equipment Mailboxes will appear as a resource when you are creating a
    meeting, and support advanced features such as automatically declining meeting invitations when there are clashes
    and double bookings.
  </p>

  <p>
    If you do not wish to use Room and Equipment Mailboxes, it is also possible to set up each meeting room as a
    <a href="https://support.office.com/en-us/article/Share-an-Outlook-calendar-with-other-people-353ed2c1-3ec5-449d-8c73-6931a0adab88">
      Shared calendar</a>.  Shared Calendars do not show up as resources when creating a meeting, and do not automatically decline
    bookings if there is a conflict, so using Room and Equipment Mailboxes is highly recommended.
  </p>

  <p class="tip icon">
    By default, Microsoft 365 labels events in resource calendars with the meeting's organizer rather than its title.  If
    you would like to see your meetings displayed in Dash with their title, you will need to connect to your Microsoft 365
    instance via PowerShell, and then change the default settings for each room.  You can find
    instructions in <a routerLink="/guide/o365-powershell">our helpful guide</a>.
  </p>

  <h2>Choose a connection method</h2>

  <p>
    There are two methods that you can use to connect Dash to Microsoft 365: a <i>Personal or service account</i> and an <i>Enterprise application</i>.
    Both offer their own advantages and tradeoffs:
  </p>

  <div class="connectionMethod">
    <h4>Enterprise Application</h4>
    <p>An Enterprise Application allows Dash to connect to Microsoft 365 on behalf of your entire organization.  This
      method must be set up by a global administrator in your Microsoft 365 organization, but offers the advantage that new
      room and equipment mailboxes will be automatically picked up by Dash without any further action.  This method is
      recommended for most organizations.
    </p>
    <ul>
      <li>Works with room and equipment mailboxes only</li>
      <li>Does not require any calendar sharing to be set up</li>
      <li>Dash connects on behalf of your entire organization</li>
      <li>Must be set up by a global administrator</li>
    </ul>

    <p class="tip">For help setting up Dash to connect to Microsoft 365 via an enterprise application, see our
      <a routerLink="/guide/o365-enterprise-application">online guide</a>.</p>
  </div>

  <div class="connectionMethod">
    <h4>Business or Personal Microsoft account (service account)</h4>
    <p>Service accounts are the most flexible way to connect Dash to Microsoft 365.  They are ideal for
    smaller teams, and are the only way to connect Dash if you're using a personal Microsoft 365 subscription.  This method
      is recommended for small businesses and customers using Dash with a personal Microsoft 365 subscription.</p>
    <ul>
      <li>Works with any type of calendar (both standard calendars, and room/equipment mailboxes)</li>
      <li>Requires calendar sharing to be set up if you want Dash to be able to see multiple meeting rooms at once</li>
      <li>Supports personal Microsoft 365 subscriptions</li>
      <li>Dash connects as a user that you specify</li>
    </ul>
    <p class="tip">For help setting up Dash to use a personal or service account, see our <a routerLink="/guide/o365-service-account">online guide</a>.</p>
  </div>



  <p>&nbsp;</p>


</div>
